/**
 * CurrencyDetailModal — View currency details with FormModal pattern.
 * Hides delete button for the default currency.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  Hash,
  Type,
  CheckCircle,
  XCircle,
  Star,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useDeleteCurrency } from '../hooks';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import toast from 'react-hot-toast';
import type { Currency } from '../types';

interface CurrencyDetailModalProps {
  currency: Currency;
  onClose: () => void;
  onEdit?: (currency: Currency) => void;
}

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-slate-400">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-neutral-500">{label}</p>
        <p className="text-sm font-medium text-neutral-800 truncate">{value || '\u2014'}</p>
      </div>
    </div>
  );
}

export function CurrencyDetailModal({ currency, onClose, onEdit }: CurrencyDetailModalProps) {
  const { t } = useTranslation();
  const deleteMutation = useDeleteCurrency();
  const { openConfirm } = useModal();
  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={`${currency.symbol} ${currency.name}`}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status */}
        <div className="rounded-xl p-3" style={{ backgroundColor: MODAL_COLORS.interactive, border: `1px solid ${MODAL_COLORS.border}` }}>
          <div className="flex items-center gap-2">
            {currency.isActive ? (
              <CheckCircle className="w-4 h-4 opacity-60" />
            ) : (
              <XCircle className="w-4 h-4 opacity-60" />
            )}
            <span className="text-sm font-bold">{t('currencies.status')}</span>
            <span
              className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
              style={{
                backgroundColor: currency.isActive ? '#ecfdf5' : '#fee2e2',
                border: '1px solid rgba(148, 163, 184, 0.3)',
              }}
            >
              {currency.isActive ? t('currencies.active') : t('currencies.inactive')}
            </span>
            {currency.isDefault && (
              <span
                className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                }}
              >
                <Star className="w-3 h-3 inline-block mr-0.5" />
                {t('currencies.default')}
              </span>
            )}
          </div>
        </div>

        {/* Details */}
        <div
          className="glass-surface rounded-2xl p-3"
          style={{ border: `1px solid ${MODAL_COLORS.border}` }}
        >
          <InfoRow icon={<Hash className="w-4 h-4" />} label={t('currencies.code')} value={currency.code} />
          <InfoRow icon={<Type className="w-4 h-4" />} label={t('currencies.symbol')} value={currency.symbol} />
          <InfoRow icon={<Type className="w-4 h-4" />} label={t('currencies.name')} value={currency.name} />
          <InfoRow icon={<Type className="w-4 h-4" />} label={t('currencies.nameEn')} value={currency.nameEn} />
          <InfoRow icon={<Hash className="w-4 h-4" />} label={t('currencies.decimalPlaces')} value={currency.decimalPlaces} />
          <InfoRow
            icon={<Star className="w-4 h-4" />}
            label={t('currencies.isDefault')}
            value={
              <span
                className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                style={{
                  backgroundColor: currency.isDefault ? '#fef3c7' : '#f3f4f6',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                }}
              >
                {currency.isDefault ? t('common.yes') : t('common.no')}
              </span>
            }
          />
        </div>

        {/* Edit */}
        {onEdit && (
          <button
            onClick={() => {
              triggerHapticFeedback();
              onEdit(currency);
              requestClose();
            }}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold transition-transform cursor-pointer hover:translate-x-[1px] hover:translate-y-[1px]"
            style={{
              backgroundColor: MODAL_COLORS.interactive,
              border: `1px solid ${MODAL_COLORS.border}`,
            }}
          >
            {t('currencies.edit')}
          </button>
        )}

        {/* Delete — hidden for default currency */}
        {!currency.isDefault && (
          <button
            onClick={() => {
              triggerHapticFeedback();
              openConfirm({
                title: t('currencies.deleteTitle'),
                message: t('currencies.deleteMessage'),
                variant: 'danger',
                confirmLabel: t('common.delete'),
                onConfirm: async () => {
                  try {
                    await deleteMutation.mutateAsync(currency.id);
                    toast.success(t('currencies.deleteSuccess'));
                    requestClose();
                  } catch {
                    toast.error(t('currencies.deleteError'));
                  }
                },
              });
            }}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer"
            style={{
              backgroundColor: 'rgba(254, 226, 226, 0.6)',
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#B91C1C',
            }}
          >
            <Trash2 className="w-4 h-4" />
            {t('common.delete')}
          </button>
        )}
      </div>
    </FormModal>
  );
}
